// Statements.tsx - 对账单管理
// ============================
import { useState, useEffect, useCallback } from 'react'
import { Table, Card, Button, Space, Modal, Form, Input, Select, InputNumber, message, Tag, Row, Col, DatePicker, Checkbox } from 'antd'
import { PlusOutlined, SearchOutlined, ReloadOutlined, EyeOutlined, CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons'
import { statementsApi, enterpriseClientsApi } from '../services/api'
import dayjs from 'dayjs'

const { Option } = Select
const { TextArea } = Input

// --- 类型定义 ---
interface StatementItem {
  id: number
  order_no: string
  order_date: string
  product_name: string
  quantity: number
  unit: string
  unit_price: number
  subtotal: number
}

interface Statement {
  id: number
  enterprise_client_id: number
  enterprise_client_name?: string
  year: number
  month: number
  statement_no: string
  total_amount: number
  total_quantity: number
  order_count: number
  status: number
  confirmed_at?: string
  confirmed_by?: number
  pdf_path?: string
  created_at: string
  items?: StatementItem[]
}

// 状态映射
const STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '待确认', color: 'orange' },
  1: { text: '已确认', color: 'green' }
}

// --- 主组件 ---
export default function Statements() {
  const [loading, setLoading] = useState(false)
  const [list, setList] = useState<Statement[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // 弹窗状态
  const [detailVisible, setDetailVisible] = useState(false)
  const [generateVisible, setGenerateVisible] = useState(false)
  const [currentStatement, setCurrentStatement] = useState<Statement | null>(null)
  const [detailItems, setDetailItems] = useState<StatementItem[]>([])

  // 下拉数据
  const [enterpriseClients, setEnterpriseClients] = useState<Array<{ id: number; company_name: string }>>([])

  // 表单
  const [form] = Form.useForm()
  const [searchForm] = Form.useForm()
  const [generateForm] = Form.useForm()

  // 加载下拉数据
  const loadRefData = useCallback(async () => {
    try {
      const res = await enterpriseClientsApi.getList({ page_size: 1000 })
      if (res.code === 0 || res.code === 200) {
        setEnterpriseClients(res.data?.list || [])
      }
    } catch (err) {
      console.error('加载企业客户失败', err)
    }
  }, [])

  // 加载列表
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const vals = searchForm.getFieldsValue()
      const res = await statementsApi.getList({ page, page_size: pageSize, ...vals })
      if (res.code === 0 || res.code === 200) {
        setList(res.data?.list || [])
        setTotal(Number(res.data?.total) || 0)
      } else {
        message.error(res.message || '加载失败')
      }
    } catch (err: any) {
      message.error(err.message || '加载失败')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, searchForm])

  useEffect(() => { loadRefData() }, [loadRefData])
  useEffect(() => { loadData() }, [loadData])

  const handleSearch = () => { setPage(1); loadData() }
  const handleReset = () => { searchForm.resetFields(); setPage(1); loadData() }

  // ---- 查看详情 ----
  const handleViewDetail = async (record: Statement) => {
    setCurrentStatement(record)
    try {
      const res = await statementsApi.getDetail(record.id)
      if (res.code === 0 || res.code === 200) {
        setDetailItems(res.data?.items || [])
        setDetailVisible(true)
      }
    } catch (err: any) {
      message.error(err.message || '加载详情失败')
    }
  }

  // ---- 生成对账单 ----
  const handleGenerate = () => {
    generateForm.resetFields()
    setGenerateVisible(true)
  }

  const handleGenerateSubmit = async () => {
    try {
      const vals = await generateForm.validateFields()
      const res = await statementsApi.generate(vals)
      if (res.code === 0 || res.code === 200) {
        message.success('生成成功')
        setGenerateVisible(false)
        loadData()
      } else {
        message.error(res.message || '生成失败')
      }
    } catch (err: any) {
      message.error(err.message || '生成失败')
    }
  }

  // ---- 确认对账单 ----
  const handleConfirm = async (record: Statement) => {
    Modal.confirm({
      title: '确认对账单',
      content: (
        <div>
          <p>确认对账单 <strong>{record.statement_no}</strong> 无误？</p>
          <p>确认后状态将变为"已确认"，企业客户可查看此对账单。</p>
        </div>
      ),
      onOk: async () => {
        try {
          const res = await statementsApi.confirm(record.id)
          if (res.code === 0 || res.code === 200) {
            message.success('确认成功')
            loadData()
          } else {
            message.error(res.message || '确认失败')
          }
        } catch (err: any) {
          message.error(err.message || '确认失败')
        }
      }
    })
  }

  // ---- 下载PDF ----
  const handleDownloadPdf = (record: Statement) => {
    if (!record.pdf_path) {
      message.warning('PDF文件未生成')
      return
    }
    const url = statementsApi.getPdfUrl(record.id)
    window.open(url, '_blank')
  }

  // ---- 表格列 ----
  const columns = [
    { title: '对账单号', dataIndex: 'statement_no', key: 'statement_no' },
    {
      title: '企业客户',
      dataIndex: 'enterprise_client_name',
      key: 'enterprise_client_name',
      render: (text: string, record: Statement) => text || `企业ID: ${record.enterprise_client_id}`
    },
    {
      title: '期间',
      key: 'period',
      render: (_: any, record: Statement) => `${record.year}年${record.month}月`
    },
    {
      title: '总金额(元)',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (val: number) => Number(val || 0).toFixed(2)
    },
    {
      title: '总数量',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      render: (val: number) => Number(val || 0).toFixed(3)
    },
    { title: '订单数', dataIndex: 'order_count', key: 'order_count' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (val: number) => {
        const s = STATUS_MAP[val] || { text: '未知', color: 'default' }
        return <Tag color={s.color}>{s.text}</Tag>
      }
    },
    {
      title: '确认时间',
      dataIndex: 'confirmed_at',
      key: 'confirmed_at',
      render: (val: string) => val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Statement) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>详情</Button>
          {record.status === 0 && (
            <Button type="link" icon={<CheckCircleOutlined />} onClick={() => handleConfirm(record)}>确认</Button>
          )}
          {record.pdf_path && (
            <Button type="link" icon={<DownloadOutlined />} onClick={() => handleDownloadPdf(record)}>下载PDF</Button>
          )}
        </Space>
      )
    }
  ]

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Form form={searchForm} layout="inline">
          <Form.Item name="enterprise_client_id" label="企业客户">
            <Select placeholder="全部" allowClear style={{ width: 200 }}>
              {enterpriseClients.map(ec => (
                <Option key={ec.id} value={ec.id}>{ec.company_name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="year" label="年份">
            <Select placeholder="全部" allowClear style={{ width: 120 }}>
              {[2024, 2025, 2026].map(y => (
                <Option key={y} value={y}>{y}年</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="month" label="月份">
            <Select placeholder="全部" allowClear style={{ width: 100 }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <Option key={m} value={m}>{m}月</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select placeholder="全部" allowClear style={{ width: 120 }}>
              <Option value={0}>待确认</Option>
              <Option value={1}>已确认</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title="对账单列表"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleGenerate}>生成对账单</Button>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) }
          }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title={`对账单详情 - ${currentStatement?.statement_no || ''}`}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={1000}
      >
        <Table
          rowKey="id"
          columns={[
            { title: '订单号', dataIndex: 'order_no', key: 'order_no' },
            {
              title: '订单日期',
              dataIndex: 'order_date',
              key: 'order_date',
              render: (val: string) => dayjs(val).format('YYYY-MM-DD')
            },
            { title: '商品名称', dataIndex: 'product_name', key: 'product_name' },
            {
              title: '数量',
              dataIndex: 'quantity',
              key: 'quantity',
              render: (val: number) => Number(val || 0).toFixed(3)
            },
            { title: '单位', dataIndex: 'unit', key: 'unit' },
            {
              title: '单价(元)',
              dataIndex: 'unit_price',
              key: 'unit_price',
              render: (val: number) => Number(val || 0).toFixed(6)
            },
            {
              title: '小计(元)',
              dataIndex: 'subtotal',
              key: 'subtotal',
              render: (val: number) => Number(val || 0).toFixed(2)
            }
          ]}
          dataSource={detailItems}
          pagination={false}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5} align="right">
                  <strong>合计：</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>{detailItems.reduce((sum, item) => sum + Number(item.subtotal || 0), 0).toFixed(2)} 元</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Modal>

      {/* 生成对账单弹窗 */}
      <Modal
        title="生成对账单"
        open={generateVisible}
        onOk={handleGenerateSubmit}
        onCancel={() => setGenerateVisible(false)}
        okText="生成"
        cancelText="取消"
      >
        <Form form={generateForm} layout="vertical">
          <Form.Item name="enterprise_client_id" label="企业客户" rules={[{ required: true, message: '请选择企业客户' }]}>
            <Select placeholder="请选择企业客户">
              {enterpriseClients.map(ec => (
                <Option key={ec.id} value={ec.id}>{ec.company_name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="year" label="年份" rules={[{ required: true, message: '请选择年份' }]}>
            <Select placeholder="请选择年份">
              {[2024, 2025, 2026].map(y => (
                <Option key={y} value={y}>{y}年</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="month" label="月份" rules={[{ required: true, message: '请选择月份' }]}>
            <Select placeholder="请选择月份">
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <Option key={m} value={m}>{m}月</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
